/* Several rate series against a shared time axis.
 *
 * Series are irregular — CAI skips Sundays, mandi quotes appear and vanish,
 * imported world prices may be weekly — so x is real elapsed time, not the
 * observation index. Plotting by index would silently compress a gap and
 * make a stale series look like it kept trading.
 */

import { useMemo } from 'react'
import { GridLines, Plot } from './Plot'
import { TipRow, TipTitle } from './hover'
import { bandValues, niceBand, padded, polyline, seriesColor, seriesDash, xScale, yScale } from '../../lib/scale'

export interface LineSeries {
  name: string
  points: { t: number; v: number }[]   // t is epoch ms
}

export function MultiLine({
  series, height = 320, labelY, yTitle, baseline,
}: {
  series: LineSeries[]
  height?: number
  labelY: (v: number) => string
  yTitle: string
  baseline?: number
}) {
  const all = series.flatMap((s) => s.points)
  const hasData = all.length > 0

  const lo0 = hasData ? Math.min(...all.map((p) => p.v)) : 0
  const hi0 = hasData ? Math.max(...all.map((p) => p.v)) : 1
  const [pLo, pHi] = padded(lo0, hi0)
  const band = niceBand(Math.max(0, pLo), pHi, 4)

  const t0 = hasData ? Math.min(...all.map((p) => p.t)) : Date.now() - 864e5
  const t1 = hasData ? Math.max(...all.map((p) => p.t)) : Date.now()

  const x = xScale(t0, t1, 1000)
  const y = yScale(band.lo, band.hi, height)

  /* Hit-testing is by day across the span, not by observation index: the
     series are irregular, so the nth point of one series is not the same
     date as the nth of another. One band per day maps the cursor to a real
     date, and each series then contributes its nearest reading to it. */
  const days = hasData
    ? Math.min(2000, Math.max(1, Math.round((t1 - t0) / 864e5)))
    : 0

  /* How far a reading may sit from the cursor and still count as "here".
     Scaled to each series' own cadence rather than fixed: a daily series
     goes stale in days, but a monthly index is never more than a fortnight
     from any date and would otherwise almost never appear. Measured from
     the median gap, so one long holiday break does not widen it. */
  const tolerance = useMemo(() => series.map((se) => {
    if (se.points.length < 2) return 7 * 864e5
    const gaps: number[] = []
    for (let i = 1; i < se.points.length; i++) gaps.push(se.points[i].t - se.points[i - 1].t)
    gaps.sort((a, b) => a - b)
    const median = gaps[Math.floor(gaps.length / 2)]
    return Math.max(7 * 864e5, median * 0.6)
  }), [series])

  const tooltip = (i: number) => {
    const t = t0 + ((i + 0.5) / days) * (t1 - t0)
    const rows = series.map((se, n) => {
      let best: { t: number; v: number } | null = null
      for (const pt of se.points) {
        if (!best || Math.abs(pt.t - t) < Math.abs(best.t - t)) best = pt
      }
      return best && Math.abs(best.t - t) <= tolerance[n]
        ? { name: se.name, v: best.v, color: seriesColor(n) }
        : null
    }).filter(Boolean) as { name: string; v: number; color: string }[]

    if (!rows.length) return null
    return (
      <>
        <TipTitle>
          {new Date(t).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
        </TipTitle>
        {rows.map((r) => (
          <TipRow key={r.name} label={r.name} value={labelY(r.v)} color={r.color} />
        ))}
      </>
    )
  }

  const xLabels = (
    <>
      {[0, 1, 2, 3, 4, 5].map((i) => (
        <span key={i} style={{ textAlign: 'center', fontSize: 11, color: 'var(--color-neutral-600)' }}>
          {new Date(t0 + ((t1 - t0) * i) / 5).toLocaleDateString('en-IN', { month: 'short', year: '2-digit' })}
        </span>
      ))}
    </>
  )

  return (
    <Plot
      height={height}
      yTitle={yTitle}
      yLabels={bandValues(band.lo, band.hi, 4).map(labelY)}
      xLabels={xLabels}
      bands={days}
      tooltip={hasData ? tooltip : undefined}
    >
      <GridLines ys={bandValues(band.lo, band.hi, 4).map(y)} />
      {baseline !== undefined && baseline >= band.lo && baseline <= band.hi ? (
        <line x1="0" y1={y(baseline)} x2="1000" y2={y(baseline)}
          stroke="var(--color-neutral-500)" strokeWidth="1" strokeDasharray="3 3"
          vectorEffect="non-scaling-stroke" />
      ) : null}
      {series.map((s, i) => (
        <polyline
          key={s.name + i}
          points={polyline(s.points.map((p) => [x(p.t), y(p.v)]))}
          fill="none"
          stroke={seriesColor(i)}
          strokeWidth="1.8"
          strokeDasharray={seriesDash(i)}
          vectorEffect="non-scaling-stroke"
        />
      ))}
    </Plot>
  )
}
